var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CHILD_FRIENDLY_OPTIONS, GUEST_POINTS_MAX, GUEST_POINTS_MIN, HOMES_OPEN_TO_OPTIONS, PRACTICAL_AMENITY_OPTIONS, REMOTE_FRIENDLY_OPTIONS, UNIQUE_AMENITY_OPTIONS } from '@homeexchange/search';
import { PLACE_LAYERS } from '../../constants';
import i18n from '../../i18n';
import Feature from '../../models/Feature';
import FiltersModel from '../../models/Filters';
import Home from '../../models/Home';
import Utils from '../../utils/utils';
var LEGACY_AMENITY_BY_LIB_VALUE = {
    'a-c': 'ac',
    playground: 'kids-playground',
    'secured-pool': 'secured-swimming-pool',
    'dedicated-workspace': 'dedicated-work-space',
    'high-speed-connexion': 'high-speed-connection'
};
var LIB_VALUE_BY_LEGACY_AMENITY = Object.keys(LEGACY_AMENITY_BY_LIB_VALUE).reduce(function (acc, libValue) {
    acc[LEGACY_AMENITY_BY_LIB_VALUE[libValue]] = libValue;
    return acc;
}, {});
var ACCESSIBILITY_AMENITY = 'disabled-access';
var PUBLIC_TRANSPORT_AMENITY = 'public-transit-access';
var PETS_WELCOME_AMENITY = 'pets-welcome';
var NO_TEMPORARY_BED_FILTER = 'no-bed-up';
var VERIFIED_HOME_FILTER = 'home-verified';
var RESPONSE_RATE_FILTER = 'response-rate-above-threshold';
var QUALITY_FILTERS = [NO_TEMPORARY_BED_FILTER, VERIFIED_HOME_FILTER, RESPONSE_RATE_FILTER];
var AMENITY_OPTIONS = [
    PRACTICAL_AMENITY_OPTIONS,
    UNIQUE_AMENITY_OPTIONS,
    CHILD_FRIENDLY_OPTIONS,
    REMOTE_FRIENDLY_OPTIONS,
    HOMES_OPEN_TO_OPTIONS
];
var MODAL_AMENITIES = AMENITY_OPTIONS.reduce(function (acc, options) {
    return acc.concat(options.map(function (option) { return toLegacyAmenity(option.value); }));
}, [ACCESSIBILITY_AMENITY, PUBLIC_TRANSPORT_AMENITY]);
function toLegacyAmenity(libValue) {
    var _a;
    return (_a = LEGACY_AMENITY_BY_LIB_VALUE[libValue]) !== null && _a !== void 0 ? _a : libValue;
}
function toLibAmenity(legacyAmenity) {
    var _a;
    return (_a = LIB_VALUE_BY_LEGACY_AMENITY[legacyAmenity]) !== null && _a !== void 0 ? _a : legacyAmenity;
}
function toArray(value) {
    if (Array.isArray(value)) {
        return value.map(String);
    }
    if (typeof value === 'string' && value !== '') {
        return value.split(',');
    }
    return [];
}
function toCounterValue(value) {
    var count = Number(value);
    return Number.isFinite(count) && count > 0 ? count : 0;
}
function toGuestPoints(value, fallback) {
    var guestPoints = Number(value);
    if (!Number.isFinite(guestPoints)) {
        return fallback;
    }
    return Math.min(Math.max(guestPoints, GUEST_POINTS_MIN), GUEST_POINTS_MAX);
}
function selectedAmenities(options, amenities) {
    return options
        .map(function (option) { return option.value; })
        .filter(function (value) { return amenities.includes(toLegacyAmenity(value)); });
}
function assignFilter(filters, name, value) {
    var isEmpty = value === undefined ||
        value === null ||
        value === false ||
        value === 0 ||
        (Array.isArray(value) && value.length === 0);
    if (isEmpty) {
        delete filters[name];
    }
    else {
        filters[name] = value;
    }
}
var REGION_LAYER = 'region';
function toReverseSearchAdminLevel(layer) {
    if (layer !== REGION_LAYER) {
        return layer;
    }
    return PLACE_LAYERS.includes('macroregion') ? 'county' : 'macroregion';
}
function toReverseSearchLocation(preferredDestination) {
    if (!preferredDestination ||
        typeof preferredDestination.address !== 'string' ||
        preferredDestination.address === '') {
        return null;
    }
    var address = preferredDestination.address, adminLevel = preferredDestination.admin_level, bounds = preferredDestination.bounds;
    return __assign({ id: address, value: address, layer: adminLevel }, (bounds && { bbox: [bounds.sw.lon, bounds.sw.lat, bounds.ne.lon, bounds.ne.lat] }));
}
function toPreferredDestination(location) {
    if (!(location === null || location === void 0 ? void 0 : location.bbox) || location.bbox.length !== 4) {
        return undefined;
    }
    return {
        bounds: Utils.convertBboxToBounds(location.bbox),
        admin_level: toReverseSearchAdminLevel(location.layer),
        address: location.value
    };
}
export function toFiltersModalState(flatFilters) {
    var _a, _b;
    var amenities = toArray(flatFilters.amenities);
    var qualityFilters = toArray(flatFilters.filters);
    var guestpoints = (_a = flatFilters.guestpoints) !== null && _a !== void 0 ? _a : {};
    return {
        onlyCollectionHomes: Boolean(flatFilters.luxury),
        exchangeTypes: toArray((_b = flatFilters.calendar) === null || _b === void 0 ? void 0 : _b.exchange_types),
        onlyFavorites: Boolean(flatFilters.favorite),
        groupIds: toArray(flatFilters.groups),
        reverseSearchHomeIds: toArray(flatFilters.reverse).map(Number),
        reverseSearchLocation: toReverseSearchLocation(flatFilters.preferred_destination),
        guestPointsRange: [
            toGuestPoints(guestpoints.from, GUEST_POINTS_MIN),
            toGuestPoints(guestpoints.to, GUEST_POINTS_MAX)
        ],
        residenceTypes: toArray(flatFilters.is_primary),
        propertyTypes: toArray(flatFilters.home_type).map(function (homeType) {
            return homeType === FiltersModel.HOME_TYPE.home ? 'house' : homeType;
        }),
        accommodationTypes: toArray(flatFilters.is_private_room).map(function (sharedPart) {
            return sharedPart === FiltersModel.IS_PRIVATE_ROOM.whole ? 'entire' : sharedPart;
        }),
        bedrooms: toCounterValue(flatFilters.bedrooms),
        bathrooms: toCounterValue(flatFilters.bathrooms),
        excludeTemporaryBeds: qualityFilters.includes(NO_TEMPORARY_BED_FILTER),
        homesOpenTo: selectedAmenities(HOMES_OPEN_TO_OPTIONS, amenities),
        isAccessible: amenities.includes(ACCESSIBILITY_AMENITY),
        surroundings: toArray(flatFilters.surrounding),
        hasPublicTransport: amenities.includes(PUBLIC_TRANSPORT_AMENITY),
        practicalAmenities: selectedAmenities(PRACTICAL_AMENITY_OPTIONS, amenities),
        uniqueAmenities: selectedAmenities(UNIQUE_AMENITY_OPTIONS, amenities),
        childFriendlyAmenities: selectedAmenities(CHILD_FRIENDLY_OPTIONS, amenities),
        remoteFriendlyAmenities: selectedAmenities(REMOTE_FRIENDLY_OPTIONS, amenities),
        isSustainable: Number(flatFilters.eco_level) === Feature.ECO_LEVEL,
        petAllergies: toArray(flatFilters.exclude_animals),
        petSitting: toArray(flatFilters.pets_to_care_for),
        onlyVerifiedHomes: qualityFilters.includes(VERIFIED_HOME_FILTER),
        onlyAboveAverageResponseRate: qualityFilters.includes(RESPONSE_RATE_FILTER)
    };
}
export function fromFiltersModalState(filtersState, flatFilters) {
    var _a, _b, _c, _d, _e, _f, _g;
    var filters = __assign({}, flatFilters);
    var amenities = toArray(flatFilters.amenities)
        .filter(function (amenity) { return !MODAL_AMENITIES.includes(amenity); })
        .concat([
        filtersState.practicalAmenities,
        filtersState.uniqueAmenities,
        filtersState.childFriendlyAmenities,
        filtersState.remoteFriendlyAmenities,
        filtersState.homesOpenTo
    ].reduce(function (acc, values) { return acc.concat(values.map(toLegacyAmenity)); }, []));
    if (filtersState.isAccessible) {
        amenities.push(ACCESSIBILITY_AMENITY);
    }
    if (filtersState.hasPublicTransport) {
        amenities.push(PUBLIC_TRANSPORT_AMENITY);
    }
    var qualityFilters = toArray(flatFilters.filters).filter(function (filter) { return !QUALITY_FILTERS.includes(filter); });
    if (filtersState.excludeTemporaryBeds) {
        qualityFilters.push(NO_TEMPORARY_BED_FILTER);
    }
    if (filtersState.onlyVerifiedHomes) {
        qualityFilters.push(VERIFIED_HOME_FILTER);
    }
    if (filtersState.onlyAboveAverageResponseRate) {
        qualityFilters.push(RESPONSE_RATE_FILTER);
    }
    var _h = filtersState.guestPointsRange, guestPointsFrom = _h[0], guestPointsTo = _h[1];
    var guestpoints = {};
    if (guestPointsFrom > GUEST_POINTS_MIN) {
        guestpoints.from = guestPointsFrom;
    }
    if (guestPointsTo < GUEST_POINTS_MAX) {
        guestpoints.to = guestPointsTo;
    }
    filters.calendar = __assign(__assign({}, ((_a = flatFilters.calendar) !== null && _a !== void 0 ? _a : {})), { exchange_types: __spreadArray([], filtersState.exchangeTypes, true) });
    filters.guestpoints = guestpoints;
    if (amenities.includes(PETS_WELCOME_AMENITY) && !(((_d = (_c = (_b = flatFilters.home) === null || _b === void 0 ? void 0 : _b.size) === null || _c === void 0 ? void 0 : _c.beds) === null || _d === void 0 ? void 0 : _d.adults) > 0)) {
        filters.home = __assign(__assign({}, flatFilters.home), { size: __assign(__assign({}, (_e = flatFilters.home) === null || _e === void 0 ? void 0 : _e.size), { beds: __assign(__assign({}, (_g = (_f = flatFilters.home) === null || _f === void 0 ? void 0 : _f.size) === null || _g === void 0 ? void 0 : _g.beds), { adults: 1 }) }) });
    }
    assignFilter(filters, 'amenities', amenities);
    assignFilter(filters, 'filters', qualityFilters);
    assignFilter(filters, 'favorite', filtersState.onlyFavorites);
    assignFilter(filters, 'is_primary', __spreadArray([], filtersState.residenceTypes, true));
    assignFilter(filters, 'home_type', filtersState.propertyTypes.map(function (propertyType) {
        return propertyType === 'house' ? FiltersModel.HOME_TYPE.home : propertyType;
    }));
    assignFilter(filters, 'is_private_room', filtersState.accommodationTypes.map(function (accommodationType) {
        return accommodationType === 'entire' ? FiltersModel.IS_PRIVATE_ROOM.whole : accommodationType;
    }));
    assignFilter(filters, 'bedrooms', filtersState.bedrooms);
    assignFilter(filters, 'bathrooms', filtersState.bathrooms);
    assignFilter(filters, 'surrounding', __spreadArray([], filtersState.surroundings, true));
    assignFilter(filters, 'eco_level', filtersState.isSustainable ? Feature.ECO_LEVEL : undefined);
    assignFilter(filters, 'exclude_animals', __spreadArray([], filtersState.petAllergies, true));
    assignFilter(filters, 'pets_to_care_for', __spreadArray([], filtersState.petSitting, true));
    assignFilter(filters, 'luxury', filtersState.onlyCollectionHomes);
    assignFilter(filters, 'groups', filtersState.groupIds.map(Number));
    assignFilter(filters, 'reverse', __spreadArray([], filtersState.reverseSearchHomeIds, true));
    assignFilter(filters, 'preferred_destination', toPreferredDestination(filtersState.reverseSearchLocation));
    return filters;
}
export function toFiltersModalGroups(groups) {
    return groups.map(function (group) {
        var _a;
        return ({
            id: String(group.id),
            name: (_a = group.label()) !== null && _a !== void 0 ? _a : '',
            iconUrl: "".concat(Utils.getAssetsDomain(), "/images/groups/group_").concat(group.id, "/logo.jpg")
        });
    });
}
export function toFiltersModalReverseHomes(homes) {
    var wholeUnits = homes.filter(function (home) { return home.get('shared_part') === Home.SHARED_PART_WHOLE_UNIT; });
    var privateRooms = homes.filter(function (home) { return home.get('shared_part') === Home.SHARED_PART_PRIVATE_ROOM; });
    return __spreadArray(__spreadArray([], wholeUnits, true), privateRooms, true).filter(function (home) { return !home.isOffline(); })
        .reduce(function (acc, home) {
        if (acc.some(function (eligibleHome) { return eligibleHome.get('address') === home.get('address'); })) {
            return acc;
        }
        return acc.concat([home]);
    }, [])
        .map(function (home) {
        var _a, _b, _c, _d;
        var descriptions = (_a = home.get('descriptions')) !== null && _a !== void 0 ? _a : [];
        var localizedTitle = descriptions.find(function (description) { return description.locale === i18n.language && description.title !== ''; });
        var fallbackTitle = descriptions.find(function (description) { return description.title !== ''; });
        return {
            homeId: Number(home.get('id')),
            homeTitle: (_c = (_b = localizedTitle === null || localizedTitle === void 0 ? void 0 : localizedTitle.title) !== null && _b !== void 0 ? _b : fallbackTitle === null || fallbackTitle === void 0 ? void 0 : fallbackTitle.title) !== null && _c !== void 0 ? _c : '',
            address: (_d = home.get('address')) !== null && _d !== void 0 ? _d : ''
        };
    });
}
